import React, { useState, useEffect } from 'react';

import {
  DialogActions,
  Dialog,
  Button,
  Snackbar,
  Typography,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Slide,
  Grid,
} from '@material-ui/core';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { TransitionProps } from '@material-ui/core/transitions';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  DataGrid,
  ColDef,
  ValueFormatterParams,
  CellValue,
} from '@material-ui/data-grid';

import { useAuth } from '../../../hooks/auth';
import { useStyles } from './styles';
import { NoRows } from '../../../components/NoRows';
import { loadCategories, deleteCategory } from './actions';
import CategoryNew from './components/Add';
import CategoryEdit from './components/Edit';
import { ButtonNew } from '../../../components/Global';
import { LoadGrid } from '../../../components/LoadGrid';

function Alert(props: AlertProps) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

function CustomNoRowsOverlay() {
  return <NoRows />;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const Categories = () => {
  const [categorias, setCategorias] = useState([]);
  const [snackBarSucesso, setSnackBarSucesso] = useState(false);
  const [snackBarErro, setSnackBarErro] = useState(false);
  const [id, setId] = useState(0);
  const [exibeMsg, setExibeMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [load, setLoad] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const [showNew, setShowNew] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const { usuario } = useAuth();
  const classes = useStyles();
  const token = localStorage.getItem('@PecaAquiDashboard:token');

  useEffect(() => {
    setLoad(true);

    async function init() {
      try {
        const response = await loadCategories(usuario.idEstabelecimento, token);
        if (response?.data) {
          setCategorias(response.data);
        }
        setLoad(false);
      } catch (error) {
        setLoad(false);
        setMsg('Algum erro ocorreu, tente novamente mais tarde!');
        setSnackBarErro(true);
      }
    }

    init();
  }, [usuario.idEstabelecimento, token]);

  const handleUpdate = async () => {
    setLoad(true);
    setCategorias([]);
    try {
      const response = await loadCategories(usuario.idEstabelecimento, token);
      if (response?.data) {
        setCategorias(response.data);
      }
      setLoad(false);
    } catch (error) {
      setLoad(false);
      setMsg('Algum erro ocorreu, tente novamente mais tarde!');
      setSnackBarErro(true);
    }
  };

  const handleShowEdit = (): void => {
    setShowEdit(true);
  };

  const handleCloseEdit = (save: boolean): void => {
    setShowEdit(false);
    if (save) {
      setSnackBarSucesso(true);
      setMsg('Cadastro editado com sucesso!');
      handleUpdate();
    }
  };

  const handleCloseNew = (save: boolean): void => {
    setShowNew(false);

    if (save) {
      setSnackBarSucesso(true);
      setMsg('Cadastro realizado com sucesso!');
      handleUpdate();
    }
  };

  const handleShowNew = () => {
    setShowNew(true);
  };

  const handleCloseSnackBar = (
    event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackBarSucesso(false);
    setSnackBarErro(false);
  };

  const handleShowMsg = (idCategoria: string | undefined | CellValue): void => {
    if (idCategoria) {
      setId(Number(idCategoria));
      setExibeMsg(true);
    }
  };

  const handleBuscaCategoria = (
    idCategoria: string | undefined | CellValue,
  ): void => {
    setId(Number(idCategoria));
    handleShowEdit();
  };

  const handleExcluir = async (): Promise<void> => {
    setSalvando(true);

    try {
      await deleteCategory(id, token);

      setSalvando(false);
      setExibeMsg(false);
      setSnackBarSucesso(true);
      setMsg('Categoria excluida com sucesso!');
      handleUpdate();
    } catch (error) {
      setSalvando(false);
      setMsg('Algum erro ocorreu, tente novamente mais tarde!');
      setSnackBarErro(true);
    }
  };

  const columns: ColDef[] = [
    {
      field: 'id',
      headerName: 'Ações',
      width: 150,
      sortable: false,
      renderCell: (params: ValueFormatterParams) => (
        <>
          <Button
            size="small"
            onClick={() => handleBuscaCategoria(params?.getValue('id'))}
          >
            <EditIcon />
          </Button>
          <Button
            color="secondary"
            size="small"
            onClick={() => handleShowMsg(params?.getValue('id'))}
          >
            <DeleteIcon />
          </Button>
        </>
      ),
    },
    { field: 'descricao', headerName: 'Descrição', width: 390 },
    { field: 'ordem', headerName: 'Ordem', width: 70 },
    { field: 'qt_pontos', headerName: 'Qt. Pontos', width: 70 },
    {
      field: 'pizza',
      headerName: 'Pizza',
      width: 70,
      valueFormatter: (params: ValueFormatterParams) =>
        params.value === 'S' ? 'Sim' : 'Não',
    },
    {
      field: 'ativo',
      headerName: 'Ativo',
      width: 70,
      valueFormatter: (params: ValueFormatterParams) =>
        params.value === 'S' ? 'Sim' : 'Não',
    },
  ];

  return (
    <>
      {/* TELA */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Typography component="h5" variant="h5" noWrap>
            Categorias
          </Typography>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} style={{ textAlign: 'right' }}>
          <ButtonNew
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleShowNew}
          >
            Novo
          </ButtonNew>
        </Grid>
      </Grid>

      <div
        style={{
          height: 500,
          width: '100%',
          background: '#fff',
          borderRadius: '4px',
          boxShadow: '0 0 14px 0 rgba(53,64,82,.05)',
          marginTop: '24px',
        }}
      >
        <DataGrid
          rows={categorias}
          columns={columns}
          loading={load}
          components={{
            noRowsOverlay: CustomNoRowsOverlay,
            loadingOverlay: LoadGrid,
          }}
        />
      </div>
      {/* FIM TELA */}

      {showNew && (
        <CategoryNew
          open={showNew}
          handleClose={handleCloseNew}
          idEstablishment={usuario.idEstabelecimento}
        />
      )}

      {showEdit && (
        <CategoryEdit
          open={showEdit}
          handleClose={handleCloseEdit}
          idEstablishment={usuario.idEstabelecimento}
          idCategory={id}
        />
      )}

      {/* INICIO DESEJA EXCLUIR */}
      {exibeMsg && (
        <Dialog
          open={exibeMsg}
          TransitionComponent={Transition}
          keepMounted
          onClose={() => setExibeMsg(false)}
          aria-labelledby="alert-dialog-slide-title"
          aria-describedby="alert-dialog-slide-description"
        >
          <DialogTitle id="alert-dialog-slide-title">Atenção!</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-slide-description">
              Deseja excluir essa categoria?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setExibeMsg(false)}
              color="secondary"
              variant="contained"
            >
              Não
            </Button>
            <div className={classes.wrapper}>
              <Button
                onClick={handleExcluir}
                color="primary"
                variant="contained"
                style={{
                  backgroundColor: '#4caf50',
                }}
                disabled={salvando}
              >
                Sim
              </Button>
              {salvando && (
                <CircularProgress
                  size={24}
                  className={classes.buttonProgress}
                />
              )}
            </div>
          </DialogActions>
        </Dialog>
      )}
      {/* FIM DESEJA EXCLUIR */}

      {/* INICIO ALERT SUCCESS */}
      <Snackbar
        open={snackBarSucesso}
        autoHideDuration={6000}
        onClose={handleCloseSnackBar}
      >
        <Alert onClose={handleCloseSnackBar} severity="success">
          {msg}
        </Alert>
      </Snackbar>
      {/* FIM ALERT SUCCESS */}

      {/* INICIO ALERT ERRO */}
      <Snackbar
        open={snackBarErro}
        autoHideDuration={6000}
        onClose={handleCloseSnackBar}
      >
        <Alert onClose={handleCloseSnackBar} severity="error">
          {msg}
        </Alert>
      </Snackbar>
      {/* FIM ALERT ERRRO */}
    </>
  );
};
